/**
 * The headset as the dashboard sees it: one source (Web Bluetooth, the Myndchime local bridge, or the
 * simulator), per-electrode contact from the signal itself, and 1 s alpha epochs. Raw blocks, epochs and
 * status changes go to listeners; the session recorder keeps the raw EEG, the alpha session the epochs.
 */
import { BluetoothSource, bluetoothAvailable } from "./muse/bluetooth-source.ts";
import { BridgeSource } from "./muse/bridge-source.ts";
import { EEG_SITES, emptyDiagnostics, type EegBlock, type MuseSource, type SourceDiagnostics, type SourceKind, type SourceStatus } from "./muse/types.ts";
import { ContactQuality, type ContactSnapshot } from "./contact-quality.ts";
import { EpochAnalyzer, type Epoch } from "./alpha.ts";
import { SimulatorSource } from "./simulator-source.ts";

/** The Myndchime (Faria) macOS app serves LibMuse EEG here. */
export const DEFAULT_BRIDGE_URL = "ws://127.0.0.1:3030";

export type DeviceInfo = {
  source: SourceKind;
  label: string;
  deviceName: string | null;
  /** "athena" (Muse S Athena), "legacy" (Muse 2 / 2016 / original Muse S), or null when not reported. */
  protocol: string | null;
  preset: string | null;
  /** Firmware, serial and preset fields the headset reported. */
  info: Record<string, unknown>;
  simulated: boolean;
};

export type DeviceSnapshot = {
  status: SourceStatus;
  source: SourceKind | null;
  connected: boolean;
  streaming: boolean;
  battery: number | null;
  contact: ContactSnapshot[];
  diagnostics: SourceDiagnostics;
  lastEpoch: Epoch | null;
};

type Listeners = {
  block: (block: EegBlock) => void;
  epoch: (epoch: Epoch) => void;
  status: (s: DeviceSnapshot) => void;
};

export class EegDevice {
  #source: MuseSource | null = null;
  #status: SourceStatus = { phase: "idle", detail: "", deviceName: null };
  #battery: number | null = null;
  #diagnostics: SourceDiagnostics = emptyDiagnostics();
  #contact = new ContactQuality(EEG_SITES);
  #epochs = new EpochAnalyzer();
  #lastEpoch: Epoch | null = null;
  #listeners: { [K in keyof Listeners]: Set<Listeners[K]> } = { block: new Set(), epoch: new Set(), status: new Set() };

  static bluetoothAvailable = bluetoothAvailable;

  on<K extends keyof Listeners>(kind: K, fn: Listeners[K]): () => void {
    this.#listeners[kind].add(fn);
    return () => this.#listeners[kind].delete(fn);
  }

  get snapshot(): DeviceSnapshot {
    const phase = this.#status.phase;
    return {
      status: this.#status,
      source: this.#source?.kind ?? null,
      connected: !!this.#source && !["idle", "error", "closed"].includes(phase),
      streaming: phase === "streaming",
      battery: this.#battery,
      contact: this.#contact.snapshot(Date.now()),
      diagnostics: this.#diagnostics,
      lastEpoch: this.#lastEpoch,
    };
  }

  get info(): DeviceInfo | null {
    const s = this.#source;
    if (!s) return null;
    const bt = s instanceof BluetoothSource ? s : null;
    return {
      source: s.kind, label: s.label, deviceName: this.#status.deviceName,
      protocol: bt?.protocol ?? (s.kind === "bridge" ? "libmuse-bridge" : s.kind === "simulator" ? "simulated" : null),
      preset: bt?.preset ?? null, info: bt?.info ?? {}, simulated: s.kind === "simulator",
    };
  }

  /**
   * Connects to a source. For Bluetooth this must run inside the click handler without an earlier await:
   * the browser only opens its device chooser during a user gesture.
   */
  connect(kind: SourceKind, bridgeUrl = DEFAULT_BRIDGE_URL): Promise<void> {
    const source: MuseSource = kind === "bluetooth" ? new BluetoothSource() : kind === "bridge" ? new BridgeSource(bridgeUrl) : new SimulatorSource();
    const previous = this.#source;
    this.#source = source;
    this.#reset();
    // LibMuse (bridge) reports 0 … 1682.8 µV; the Bluetooth decoders centre on 0 (±724 µV for Athena).
    this.#contact.configure({ rails: kind === "bridge" ? [0, 1682.8] : [-724, 724] });
    const connecting = source.connect({
      eeg: (block) => this.#onBlock(source, block),
      optics: () => {},
      ppg: () => {},
      battery: (percent) => { if (this.#source === source) { this.#battery = percent; this.#emitStatus(); } },
      status: (status) => { if (this.#source === source) { this.#status = status; this.#emitStatus(); } },
      diagnostics: (patch) => { if (this.#source === source) this.#diagnostics = { ...this.#diagnostics, ...patch }; },
    });
    if (previous) void previous.disconnect().catch((e) => console.warn("[eeg] previous source did not disconnect cleanly:", e));
    return connecting.catch((e) => {
      if (this.#source === source) {
        this.#source = null;
        this.#status = { phase: "error", detail: e instanceof Error ? e.message : String(e), deviceName: null };
        this.#emitStatus();
      }
      throw e;
    });
  }

  async disconnect(): Promise<void> {
    const s = this.#source;
    this.#source = null;
    if (s) await s.disconnect().catch((e) => console.warn("[eeg] disconnect failed:", e));
    this.#status = { phase: "closed", detail: "", deviceName: null };
    this.#reset();
    this.#emitStatus();
  }

  #reset() {
    this.#battery = null;
    this.#diagnostics = emptyDiagnostics();
    this.#contact.reset();
    this.#epochs.reset();
    this.#lastEpoch = null;
  }

  #onBlock(source: MuseSource, block: EegBlock) {
    if (this.#source !== source) return;
    const now = Date.now();
    this.#contact.ingest(block.samples, now);
    for (const fn of this.#listeners.block) fn(block);
    const contact = this.#contact.snapshot(now).map((c) => c.status);
    for (const epoch of this.#epochs.push(block, contact)) {
      this.#lastEpoch = epoch;
      for (const fn of this.#listeners.epoch) fn(epoch);
    }
  }

  #emitStatus() {
    const snap = this.snapshot;
    for (const fn of this.#listeners.status) fn(snap);
  }
}
